import { useEffect, useState } from "react";

import apiSpec from "../benepay-b2b-api.json";
import { useLocation } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coy } from "react-syntax-highlighter/dist/esm/styles/prism";

//Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from "@fortawesome/free-solid-svg-icons";

//Utils
import { copyToClipboard } from "utils/commonUtility";
import BenepayData from "../benepay-data.json";

export default function RequestEncryption() {
    const location = useLocation();

    const [formFields, setFormFields] = useState([]);
    const [requiredFields, setRequiredFields] = useState([]);
    const [copied, setCopied] = useState(false);
    const [encCopied, setEncCopied] = useState(false);
    const [decCopied, setDecCopied] = useState(false);


    const handleEncCopy = (text) => {
        copyToClipboard(text, setEncCopied);
    }

    const handleDecCopy = (text) => {
        copyToClipboard(text, setDecCopied);
    }

    useEffect(() => {
        // Clean path by removing the leading slash
        const cleanPath = location.pathname.startsWith("/")
            ? location.pathname.substring(1)
            : location.pathname;

        if (!apiSpec?.paths) return;

        Object.entries(apiSpec.paths).forEach(([apiPath, values]) => {
            const reqMethod = values.post ? "post" : values.get ? "get" : null;

            if (cleanPath === "encryption") {

                // Extract request body schema dynamically
                const schemaRef = values[reqMethod]?.requestBody?.content?.["application/json"]?.schema?.$ref;
                if (schemaRef) {
                    const schemaName = "SubmitRequestToPayV2";
                    const schema = apiSpec.components.schemas[schemaName];

                    if (schema) {
                        const properties = schema?.properties || {};
                        const requiredFields = schema?.required || [];

                        setFormFields(Object.entries(properties));
                        setRequiredFields(requiredFields);

                    }
                } else {
                    setFormFields([]);
                    setRequiredFields([]);
                }
            }
        });

    }, [apiSpec, location.pathname]);

    const handleCopy = () => {
        navigator.clipboard.writeText(javaCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleScrollTo = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" }); // Smooth scrolling effect
        }
    };

    const sampleRequest = {
        "collectionAmountCurrency": "INR",
        "collectionReferenceNumber": "1dff0def-80b6-402c-bf2c-8b82f01b3ec8",
        "debtorEmailId": "sam@gmail.com",
        "debtorName": "Sam",
        "finalDueAmount": "10",
        "requestorTransactionId": "two-mid-test-16",
        "debtorMobileNumber": "+91-6374133569",
        "reasonForCollection": "OnlinePayment",
        "initialDueAmount": "10",
        "initialDueDate": "2024-12-12",
        "charges": "0",
        "reasonForCharges": "NA",
        "finalDueDate": "2025-12-12",
        "additionalComments": "Add Comments",
        "payVia": [],
        "allowPartialPayments": false
    };

    const javaCode = `
    import javax.crypto.Cipher;
    import javax.crypto.SecretKey;
    import javax.crypto.spec.GCMParameterSpec;
    import javax.crypto.spec.SecretKeySpec;
    import java.nio.charset.StandardCharsets;
    import java.security.SecureRandom;
    import java.util.Base64;

    public class BenePaySecurity {

    private static final String SECRET_KEY = "your_encryption_key";
    private static final int GCM_TAG_LENGTH = 128;
    private static final int IV_LENGTH = 12;

        public static String encrypt(String data, String secretKey) {
            try {
                SecureRandom random = new SecureRandom();
                byte[] iv = new byte[IV_LENGTH];
                random.nextBytes(iv);

                Cipher cipher = Cipher.getInstance("AES/GCM/NoPadding");
                GCMParameterSpec gcmSpec = new GCMParameterSpec(GCM_TAG_LENGTH, iv);

                SecretKey key = hexToAESKey(secretKey);

                cipher.init(Cipher.ENCRYPT_MODE, key, gcmSpec);
                byte[] encryptedBytes = cipher.doFinal(data.getBytes(StandardCharsets.UTF_8));

                byte[] combined = new byte[iv.length + encryptedBytes.length];
                System.arraycopy(iv, 0, combined, 0, iv.length);
                System.arraycopy(encryptedBytes, 0, combined, iv.length, encryptedBytes.length);

                return Base64.getUrlEncoder().encodeToString(combined);
            } catch (Exception e) {
                System.out.println("Error occurred during encryption: " + e);
            }
            return null;
        }

        public static String decrypt(String encryptedData, String secretKey) {
            try {
                byte[] combined = Base64.getUrlDecoder().decode(encryptedData);
                byte[] iv = new byte[IV_LENGTH];
                byte[] encryptedBytes = new byte[combined.length - IV_LENGTH];

                System.arraycopy(combined, 0, iv, 0, IV_LENGTH);
                System.arraycopy(combined, IV_LENGTH, encryptedBytes, 0, encryptedBytes.length);

                SecretKey key = hexToAESKey(secretKey);

                Cipher cipher = Cipher.getInstance("AES/GCM/NoPadding");
                GCMParameterSpec gcmSpec = new GCMParameterSpec(GCM_TAG_LENGTH, iv);
                cipher.init(Cipher.DECRYPT_MODE, key, gcmSpec);

                byte[] decryptedBytes = cipher.doFinal(encryptedBytes);
                return new String(decryptedBytes, StandardCharsets.UTF_8);
            } catch (Exception e) {
                System.out.println("Error occurred during decryption: " + e);
            }
            return null;
        }

        public static SecretKey hexToAESKey(String hexKey) {
            byte[] decodedKey = hexStringToByteArray(hexKey);
            return new SecretKeySpec(decodedKey, "AES");
        }

        /**
         * Helper function to convert hex string to byte array
         */
        private static byte[] hexStringToByteArray(String s) {
            int len = s.length();
            byte[] data = new byte[len / 2];
            for (int i = 0; i < len; i += 2) {
                data[i / 2] = (byte) ((Character.digit(s.charAt(i), 16) << 4) + Character.digit(s.charAt(i + 1), 16));
            }
            return data;
        }

        public static void main(String[] args) {
            String sampleRequest = "{\r" +
                \"requestorTransactionId\": \"Bene-001\",\r+
                \"debtorName\": \"test\",\r+
                \"debtorEmailId\": \"\",\r+
                \"debtorMobileNumber\": \"+91-9876543210\",\r+
                \"collectionReferenceNumber\": \"collRef01\",\r+
                \"reasonForCollection\": \"premium\",\r+
                \"initialDueAmount\": 120,\r+
                \"initialDueDate\": \"2025-02-25\",\r+
                \"charges\": 20,\r+
                \"reasonForCharges\": \"GST\",\r+
                \"finalDueAmount\": 140,\r+
                \"finalDueDate\": \"2025-02-25\",\r+
                \"collectionAmountCurrency\": \"INR\",\r+
                \"additionalComments\": \"test\"\r+
            "}";
            String encryptedData = encrypt(sampleRequest, SECRET_KEY);
            String decryptedData = decrypt(encryptedData, SECRET_KEY);

            System.out.println("Original value: " + sampleRequest);
            System.out.println("Encrypted value: " + encryptedData);
            System.out.println("Decrypted value: " + decryptedData);
        }
    }
    `;


    return (
        <>
            <div class="grid grid-cols-12 gap-1">
                <div class="col-span-12 md:col-span-8 gap-1" id="Introduction">
                    <div>
                        <p className="font-bold text-[32px]">API Security</p>
                    </div>

                    <div>
                        <hr className="border-t border-gray-300 my-2 w-full" />
                    </div>

                    <div>
                        <div className="col-span-8 md:col-span-8">
                            <p className="text-gray-700">
                                BeneCollect implements robust security measures to protect sensitive payment data. Our API endpoints for Generated Payment Requests and Online Payments require request payload encryption to ensure secure transmission of payment information.
                            </p>

                            <div className="mt-6">
                                <p className="text-gray-700">
                                    Key security features include:
                                </p>
                                <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                                    <li>AES-256 encryption for request/response payloads</li>
                                    <li>Unique encryption keys per merchant</li>
                                    <li>Secure key rotation policies</li>
                                    <li>Request signature validation</li>
                                </ul>
                            </div>

                            <div className="space-y-3 mt-8">
                                <div className="p-4 bg-gray-50 border-l-4 border-blue-500 rounded">
                                    <h3 className="font-semibold text-gray-800">Encryption Requirements</h3>
                                    <p className="text-gray-600 mt-2">
                                        All requests to payment endpoints must have their payloads encrypted using the provided merchant encryption key. The encrypted data should be BASE64 encoded and URL-safe for transmission.
                                    </p>
                                </div>

                                <div className="p-4 bg-gray-50 border-l-4 border-green-500 rounded">
                                    <h3 className="font-semibold text-gray-800">Implementation Steps</h3>
                                    <ol className="list-decimal pl-5 mt-2 text-gray-600 space-y-2">
                                        <li>Obtain your unique encryption key from the BenePay merchant portal</li>
                                        <li>Structure your request payload according to the API specifications</li>
                                        <li>Encrypt the payload using AES-GCM</li>
                                        <li>Convert the encrypted binary data to URL-safe BASE64 format</li>
                                        <li>Send the encrypted payload in the 'encryptedData' field</li>
                                        <li>On receiving the encrypted response, decrypt the payload using the same AES-GCM method and your encryption key to obtain the final response values.</li>
                                    </ol>
                                </div>

                                <div className="p-4 bg-gray-50 border-l-4 border-green-500 rounded">
                                    <h3 className="font-semibold text-gray-800">Decryption Process</h3>
                                    <p className="text-gray-600 space-y-2 mt-1">To decrypt, extract the IV from the response.</p>
                                    <ol className="list-decimal pl-5 mt-2 text-gray-600 space-y-2">
                                        <li><strong>Initialization Vector (IV) (First 12 bytes)</strong> – A unique nonce used for encryption.</li>
                                        <li><strong>Ciphertext (Remaining bytes excluding the authentication tag)</strong> – The encrypted payment data.</li>
                                        <li><strong>Authentication Tag (Last 16 bytes)</strong> – Ensures data integrity and authenticity.</li>
                                    </ol>
                                </div>

                                <div className="p-4 bg-gray-50 border-l-4 border-yellow-500 rounded">
                                    <h3 className="font-semibold text-gray-800">Security Best Practices</h3>
                                    <ul className="mt-2 text-gray-600 space-y-2">
                                        <li className="flex items-start">
                                            <span className="text-yellow-500 mr-2">⚠️</span>
                                            Never share your encryption keys or include them in client-side code
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-yellow-500 mr-2">⚠️</span>
                                            Implement secure key storage on your servers
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-yellow-500 mr-2">⚠️</span>
                                            Rotate encryption keys periodically as per security policy
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-yellow-500 mr-2">⚠️</span>
                                            Always validate decrypted responses before processing
                                        </li>
                                    </ul>
                                </div>

                                <div className="p-4 bg-gray-50 border-l-4 border-red-500 rounded">
                                    <h3 className="font-semibold text-gray-800">Common Issues & Solutions</h3>
                                    <div className="mt-2 space-y-3">
                                        <div>
                                            <p className="text-gray-800 font-medium">Invalid Encryption Error</p>
                                            <p className="text-gray-600">Ensure you're using the correct encryption key and initialization vector (IV)</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-800 font-medium">Malformed Payload</p>
                                            <p className="text-gray-600">Verify the JSON structure matches the API specification before encryption</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-800 font-medium">Decryption Failures</p>
                                            <p className="text-gray-600">Check for proper BASE64 encoding/decoding and URL-safe character handling</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {formFields.length > 0 && (
                            <div className="p-4 mt-6 bg-gray-50 rounded" id="RequestParameters">
                                <h3 className="font-semibold">Request Parameters</h3>
                                <div className="overflow-x-auto mt-3">
                                    <table className="w-full border border-gray-200 mt-3">
                                        <thead>
                                            <tr className="bg-gray-100 border border-gray-200">
                                                <th className="p-3 font-semibold border border-gray-200">Field</th>
                                                <th className="p-3 font-semibold border border-gray-200 w-32">Data Type</th>
                                                <th className="p-3 font-semibold border border-gray-200">Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formFields.map(([fieldName, fieldDetails]) => (
                                                <tr key={fieldName} className="border">
                                                    <td className="p-3 border">
                                                        <p className="text-sm text-gray-600"><span className="font-bold">{fieldName}</span></p>
                                                        <p className="text-sm">
                                                            <span className="text-red-500">{requiredFields.includes(fieldName) ? "Required" : "Optional"}</span>
                                                        </p>
                                                    </td>
                                                    <td className="p-3 border">
                                                        <span className="pl-3">{fieldDetails.type}</span>
                                                    </td>
                                                    <td className="p-3 border">
                                                        <p className="text-sm text-gray-600">{fieldDetails.description}</p>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="p-4 mt-8 bg-gray-50 rounded">
                        <div className="flex justify-between items-center" id="JavaEncryptionDecryptionCode">
                            <h3 className="font-semibold">Java Encryption & Decryption Code</h3>
                            <button
                                onClick={handleCopy}
                                className="flex items-center bg-gray-200 px-3 py-1 rounded-md text-gray-600 hover:bg-gray-300 hover:text-gray-900 transition"
                            >
                                {!copied && <p className="font-bold">Copy</p>}
                                {copied && <span className="text-xs ml-1 text-green-600">Copied!</span>}
                            </button>
                        </div>
                        
                        <SyntaxHighlighter language="java" style={coy}>
                            {javaCode}
                        </SyntaxHighlighter>
                    </div>

                    <div className="p-4 mt-8 bg-gray-50 rounded">
                        <div className="flex justify-between items-center" id="JavaEncryptionDecryptionCode">
                            <h3 className="font-semibold">Encryption & Decryption Codes for Other Languages</h3>
                        </div>

                        <div className="flex flex-wrap -mx-2">
                            {BenepayData.encryptAndDecryptLogicFiles.languages.map((item, index) => {
                                const language = item.language;
                                const fileUrl = item.url;
                                const logo = item.logo || '';
                                
                                return (
                                    <div key={language + index} className="w-full sm:w-1/2 lg:w-1/3 px-2 mt-4">
                                        <div className="bg-white rounded-xl shadow-lg p-6 w-full h-full">
                                            <div className="flex justify-center items-center">
                                                <img
                                                    className="h-28 w-28 object-contain"
                                                    src={`images/${logo}`}
                                                    alt={`${language} logo`}
                                                />
                                            </div>
                                            <div className="text-center mt-4">
                                                <h2 className="text-xl font-bold text-gray-800">{language}</h2>
                                            </div>
                                            <div className="mt-6 flex justify-end space-x-3">
                                                <button
                                                    className={`w-full ${fileUrl ? "bg-blue-500" : "bg-gray-400"} text-white px-4 py-2 rounded ${fileUrl ? "hover:bg-blue-600" : "bg-gray-400 cursor-not-allowed"} transition flex items-center justify-center space-x-2`}
                                                    onClick={() => {
                                                        if (!fileUrl) return;
                                                        const link = document.createElement("a");
                                                        link.href = fileUrl;
                                                        link.download = fileUrl.split('/').pop();
                                                        link.target = "_blank";
                                                        link.rel = "noopener noreferrer";
                                                        link.click();
                                                    }}
                                                >
                                                    <span>{fileUrl ? "Download" : "Comming Soon ..."}</span>
                                                    {fileUrl && <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2"
                                                        viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path strokeLinecap="round" strokeLinejoin="round"
                                                            d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3">
                                                        </path>
                                                    </svg>}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>

                    <div class="p-4 mt-8 bg-gray-50 border-l-4 border-green-500 rounded" id="SampleResponse">
                        <div className="flex justify-between items-center">
                            <h3 class="font-semibold text-gray-800">Sample Encrypted Data</h3>
                            <button
                                onClick={() => handleEncCopy("BCqkwW5RD-eHdpi3kvWtEKjUQl29jY8L_nWBNhEe-0E4GQgLZ7ePzr-rW2K0zQKBIOLeIk8PQD8p5qUzu0VEdfHmjv3FprfkCk-Tb_kj6BU6NDVZ5vN7arb1OOpPZTPnxwG5WjENJGxEs5R8ZWI")}
                                className="text-gray-700 px-2 py-1 rounded text-sm"
                            >
                                {!encCopied && <p className="font-bold text-lg"><FontAwesomeIcon className="" icon={faCopy} /></p>}
                                {encCopied && <span className="text-md ml-1 text-green-600 font-bold">Copied!</span>}
                            </button>
                        </div>

                        <div className="overflow-x-auto mt-3">
                            <p class="text-base text-gray-600 mt-3">{`BCqkwW5RD-eHdpi3kvWtEKjUQl29jY8L_nWBNhEe-0E4GQgLZ7ePzr-rW2K0zQKBIOLeIk8PQD8p5qUzu0VEdfHmjv3FprfkCk-Tb_kj6BU6NDVZ5vN7arb1OOpPZTPnxwG5WjENJGxEs5R8ZWIuvH6o1HpQENRK3mYHwPjUXKmPAWudQDjdzzzXGT6HDecGbQ7Mc4W-tjDFDxd5T44WJoPSu6qY2tYDCQHI6CsBdbTFMLm2Fo7Nmj3Ju5X8Fb8h4rtFEWZPMZEhbE_Q1pG6UG05JxNWUGvTJ7VM51cE3NK58OH-KZiFXle9AYyY89kBh-nz68r8iTlmVJGOlfcxHF1b0y6YoaEp8PxnGex3O0ne5gJdgdH_aRuLcvNknJ6YmOb_05x40Hw6LS7Le3StT5e-lsjwdVWKrdboFfEsAmIxX9o4fT-HSOKQ6H-jY4BlOiUY4XertK9Go7ogit_8V9712CHsab8SjhFVEGy-6YWvD3XCxiNIoJGBUE-jmtuz55wyV927rDpWy2ZNePdnrij_u_f9-8B5eWbvFpzcK4vIIP8FpX_h-h5tyl33LF8NBG658D8q26IZ1AiIJOE9XGrUZXJGpnCiRea5T0T8cG6HE8KPnnaefKCvb1pkuVR`}</p>
                        </div>
                    </div>

                    <div className="p-4 mt-8 bg-gray-50 border-l-4 border-blue-500 rounded" id="SampleRequest">
                        <div className="flex justify-between items-center">
                            <h3 className="font-semibold text-gray-800">Sample Decrypted Data</h3>
                            <button
                                onClick={() => handleDecCopy(JSON.stringify(sampleRequest, null, 2))}
                                className="text-gray-700 px-2 py-1 rounded text-sm"
                            >
                                {!decCopied && <p className="font-bold text-lg"><FontAwesomeIcon className="" icon={faCopy} /></p>}
                                {decCopied && <span className="text-md ml-1 text-green-600 font-bold">Copied!</span>}
                            </button>
                        </div>

                        <div className="mt-4 overflow-x-auto rounded-lg p-3">
                            <pre className="whitespace-pre-wrap text-sm w-max">
                                {JSON.stringify(sampleRequest, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>

                <div class="hidden md:col-span-4 md:block">
                    <div className="sticky top-4 p-4">
                        <p className="text-gray-700 font-bold pl-5">TABLE OF THE CONTENT</p>
                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("Introduction")}>Introduction</button>
                        </div>

                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("RequestParameters")}>Request Parameters</button>
                        </div>

                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("JavaEncryptionDecryptionCode")}>Java Encryption & Decryption Code</button>
                        </div>

                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("SampleRequest")}>Sample Request</button>
                        </div>

                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("SampleResponse")}>Sample Response</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}